import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, List, Button, Modal, Form, Input, message, Spin, Empty, Tag, Pagination } from 'antd';
import { EditOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../../../auth/AuthContext';

const { Title } = Typography;
const PAGE_SIZE = 5;

const Dictionary = () => {
  const { user } = useAuth();
  const [dictionaries, setDictionaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteAllModalVisible, setDeleteAllModalVisible] = useState(false);
  const [currentDictionary, setCurrentDictionary] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editForm] = Form.useForm();
  const [addForm] = Form.useForm();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchDictionaries = useCallback(async () => {
    setLoading(true);
    try {
      if (!user || !user.access_token) {
        message.error('Authentication token not found. Please log in again.');
        setLoading(false);
        return;
      }
      const token = user.access_token;
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dictionary/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`Error fetching dictionaries: ${response.status}`);
      }
      const data = await response.json();
      if (data && data.dictionary) {
        setDictionaries(data.dictionary);
      } else {
        setDictionaries([]);
      }
    } catch (error) {
      console.error('Error fetching dictionaries:', error);
      message.error("You don't have any existing dictionary");
      setDictionaries([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDictionaries();
  }, [fetchDictionaries]);

  // Reset current page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleEdit = (dictionary) => {
    setCurrentDictionary(dictionary);
    editForm.setFieldsValue({
      replace: dictionary.replace,
      with: dictionary.with
    });
    setEditModalVisible(true);
  };

  // Using PATCH for update as per your API instructions
  const handleSave = async (values) => {
    setSubmitting(true);
    try {
      if (!user || !user.access_token || !currentDictionary) {
        message.error('Authentication token not found or no dictionary selected. Please log in again.');
        setSubmitting(false);
        return;
      }
      const token = user.access_token;
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/dictionary/me/${currentDictionary.key}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'accept': 'application/json'
          },
          body: JSON.stringify({
            replace: values.replace,
            with_: values.with
          })
        }
      );
      if (!response.ok) {
        throw new Error(`Error updating dictionary entry: ${response.status}`);
      }
      const data = await response.json();
      if (data && data.message) {
        message.success(`Dictionary entry "${currentDictionary.replace}" updated successfully`);
        setEditModalVisible(false);
        fetchDictionaries();
      }
    } catch (error) {
      console.error('Error updating dictionary entry:', error);
      message.error('Failed to update dictionary entry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdd = () => {
    addForm.resetFields();
    setAddModalVisible(true);
  };

  const handleDelete = (dictionary) => {
    setCurrentDictionary(dictionary);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    setSubmitting(true);
    try {
      if (!user || !user.access_token || !currentDictionary) {
        message.error('Authentication token not found or no item selected. Please try again.');
        setSubmitting(false);
        setDeleteModalVisible(false);
        return;
      }
      const token = user.access_token;
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/dictionary/me/${currentDictionary.key}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'accept': 'application/json'
          }
        }
      );
      if (!response.ok) {
        throw new Error(`Error deleting dictionary entry: ${response.status}`);
      }
      const data = await response.json();
      if (data && data.message) {
        message.success(`Dictionary entry "${currentDictionary.replace}" deleted successfully`);
        setDictionaries(dictionaries.filter(item => item.key !== currentDictionary.key));
        setDeleteModalVisible(false);
      }
    } catch (error) {
      console.error('Error deleting dictionary entry:', error);
      message.error('Failed to delete dictionary entry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeleteAll = async () => {
    setSubmitting(true);
    try {
      if (!user || !user.access_token) {
        message.error('Authentication token not found. Please log in again.');
        setSubmitting(false);
        return;
      }
      const token = user.access_token;
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/dictionary/me`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'accept': 'application/json'
          }
        }
      );
      if (!response.ok) {
        throw new Error(`Error deleting all dictionary entries: ${response.status}`);
      }
      const data = await response.json();
      if (data && data.message) {
        message.success('All dictionary entries deleted successfully');
        setDictionaries([]);
        setDeleteAllModalVisible(false);
      }
    } catch (error) {
      console.error('Error deleting all dictionary entries:', error);
      message.error('Failed to delete all dictionary entries. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (!user || !user.access_token) {
        message.error('Authentication token not found. Please log in again.');
        setSubmitting(false);
        return;
      }
      const token = user.access_token;
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/dictionary/me`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'accept': 'application/json'
          },
          body: JSON.stringify({
            replace: values.replace,
            with_: values.with
          })
        }
      );
      if (!response.ok) {
        throw new Error(`Error adding dictionary entry: ${response.status}`);
      }
      const data = await response.json();
      if (data && data.item_key) {
        message.success('New dictionary entry added successfully');
        setAddModalVisible(false);
        fetchDictionaries();
      }
    } catch (error) {
      console.error('Error adding dictionary entry:', error);
      message.error('Failed to add dictionary entry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter dictionaries based on search input
  const filteredDictionaries = dictionaries.filter(item =>
    item.replace.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.with.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Show latest entries first (reverse the order)
  const sortedDictionaries = [...filteredDictionaries].reverse();
  const totalItems = sortedDictionaries.length;
  const paginatedDictionaries = sortedDictionaries.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div>
      <Card 
        title={<Title level={4}>Your Dictionary</Title>} 
        bordered={false}
        style={{ boxShadow: "none" }}
        extra={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              Add Entry
            </Button>
            <Button 
              type="primary" 
              danger
              icon={<DeleteOutlined />}
              onClick={() => setDeleteAllModalVisible(true)}
            >
              Delete All
            </Button>
          </div>
        }
      >
        {/* Top row for search and pagination */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Input.Search 
            placeholder="Search entries" 
            allowClear 
            onChange={(e) => setSearchQuery(e.target.value)} 
            style={{ maxWidth: '300px' }}
          />
          <Pagination
            current={currentPage}
            pageSize={PAGE_SIZE}
            total={totalItems}
            onChange={(page) => setCurrentPage(page)}
            showSizeChanger={false}
          />
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        ) : paginatedDictionaries.length === 0 ? (
          <Empty 
            description="No dictionary entries found" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={paginatedDictionaries}
            renderItem={(item) => (
              <List.Item>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <span style={{ flex: 1, marginRight: '16px', overflow: 'hidden' }}>
                    <Tag 
                      color="red" 
                      title={item.replace}
                    >
                      {item.replace}
                    </Tag> replaced with <Tag 
                      color="green"
                      style={{ 
                        maxWidth: '80%', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        display: 'inline-block'
                      }}
                      title={item.with}
                    >
                      {item.with}
                    </Tag>
                  </span>
                  <div>
                    <Button 
                      type="text" 
                      icon={<EditOutlined />} 
                      onClick={() => handleEdit(item)}
                    />
                    <Button 
                      type="text" 
                      danger
                      icon={<DeleteOutlined />} 
                      onClick={() => handleDelete(item)}
                    />
                  </div>
                </div>
              </List.Item>
            )}
          />
        )}
      </Card>

      <Modal
        title="Edit Dictionary Entry"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} onFinish={handleSave} layout="vertical">
          <Form.Item
            name="replace"
            label="Replace"
            rules={[{ required: true, message: 'Please enter the word to replace' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="with"
            label="With"
            rules={[{ required: true, message: 'Please enter the replacement word' }]}
          >
            <Input.TextArea rows={5} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add Dictionary Entry"
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={addForm} onFinish={handleAddSubmit} layout="vertical">
          <Form.Item
            name="replace"
            label="Replace"
            rules={[{ required: true, message: 'Please enter the word to replace' }]}
          >
            <Input placeholder="e.g., protien" />
          </Form.Item>
          <Form.Item
            name="with"
            label="With"
            rules={[{ required: true, message: 'Please enter the replacement word' }]}
          >
            <Input.TextArea placeholder="e.g., protein" rows={5} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Add Entry
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Delete Dictionary Entry"
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setDeleteModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger
            loading={submitting}
            onClick={confirmDelete}
          >
            Delete
          </Button>
        ]}
      >
        {currentDictionary && (
          <p>
            Are you sure you want to delete the dictionary entry <Tag color="red">{currentDictionary.replace}</Tag> replaced with <Tag color="green"
              style={{ 
                maxWidth: '90%', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap',
                display: 'inline-block'
              }}>{currentDictionary.with}</Tag>? 
            This action cannot be undone.
          </p>
        )}
      </Modal>

      <Modal
        title="Delete All Dictionary Entries"
        open={deleteAllModalVisible}
        onCancel={() => setDeleteAllModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setDeleteAllModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger
            loading={submitting}
            onClick={confirmDeleteAll}
          >
            Delete All
          </Button>
        ]}
      >
        <p>
          Are you sure you want to delete all dictionary entries? This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default Dictionary;
